import React, { useContext, useEffect, useMemo, useState } from 'react';

import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";


import { useLicences } from "../../../context/LicenceContext";

// Api.
import HostApi from '../../../api/host/HostApi';
import InstanceApi from '../../../api/instance/InstanceApi';

// Context.
import { InstanceContext } from '../../../context/InstanceContext';

// Helpers.
import Helper from '../../../helpers/Helper';
import { getConvertedTimeToUTC } from "../../../helpers/utils";
import TimeSeriesChart from "../../../component/chart/echarts/TimeSeriesChart";
import { EChartPoint, EChartTimeSeries } from "../../../component/chart/echarts/types";

// Constants.
import { CHART_DATA_TYPE, DATA_INITIALISING, DATA_LOADED, DATA_LOADING } from '../../../component/Constants';
import { COLOUR_HOST_PROCESSOR, COLOUR_HOST_MEMORY } from '../../../component/Constants';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import HostTarget from '../../../types/host/HostTarget';
import ChartPlotLine from '../../../types/ChartPlotLine';
import HostStatisticV2 from '../../../types/host/HostStatisticV2';

import HostsDataTable from '../../../types/host/tables/HostsDataTable';
import InstanceTargetHost from '../../../types/instance/InstanceTargetHost';

// Components.
import WidgetCard from '../../../component/instance/widget';
import HostsTable from '../../../component/host/table/HostsTable';

function Hosts(props: { instance: InstanceTarget, hosts: HostTarget[], chartPlotLines: ChartPlotLine[] }) {
    const {instances} = useContext(InstanceContext);
    const {licences} = useLicences();
    const [loading, setLoading] = useState<number>(DATA_INITIALISING);
    const [tableLoading, setTableLoading] = useState<number>(DATA_INITIALISING);
    const [hostsDataTable, setHostsDataTable] = useState<HostsDataTable[]>([]);
    const [processorSeries, setProcessorSeries] = useState<EChartTimeSeries[]>([]);
    const [memorySeries, setMemorySeries] = useState<EChartTimeSeries[]>([]);
    const [processorMetric, setProcessorMetric] = useState<number>(0);
    const [processorMetricPrevious, setProcessorMetricPrevious] = useState<number | null>(null);
    const [memoryMetric, setMemoryMetric] = useState<number>(0);
    const [memoryMetricPrevious, setMemoryMetricPrevious] = useState<number | null>(null);
    const timeRangeContext = useContext(TimeRangeContext)

    const comparisonSupported = Helper.isComparisonSupported(timeRangeContext)

    const memoryPeak = useMemo(() => {
        let peaks: number[] = []
        memorySeries.forEach((serie: any) => {
            peaks = peaks.concat(serie.data.map((i: any) => i[1]))
        })
        return Helper.getPeakValue(peaks)
    }, [memorySeries])

    const processorPeak = useMemo(() => {
        let peaks: number[] = []
        processorSeries.forEach((serie: any) => {
            peaks = peaks.concat(serie.data.map((i: any) => i[1]))
        })
        return Helper.getPeakValue(peaks)
    }, [processorSeries])

    // Single pass that does all of: widget aggregate, per-host chart series, and table rows.
    useEffect(() => {
        let active = true;

        async function load() {
            if (props.hosts.length === 0) {
                setLoading(DATA_LOADED);
                setTableLoading(DATA_LOADED);
                setHostsDataTable([]);
                setProcessorSeries([]);
                setMemorySeries([]);
                setProcessorMetric(0);
                setMemoryMetric(0);
                return;
            }

            setLoading(DATA_LOADING);
            setTableLoading(DATA_LOADING);

            const query = buildApiQuery(timeRangeContext.timeRange);
            const prevQuery = buildApiQuery(timeRangeContext.timeRange, true);
            const hostIds = props.hosts.map(h => h.id);

            // Restrict the cross-host hostMappings call to just this instance.
            const hostMappingsPromise = InstanceApi.getInstanceHostByInstance(props.instance.id);
            const allMappingsPromise = InstanceApi.getInstanceHostMapping();
            const perHostSummaryPromise = HostApi.getHostStatisticV2ById(query, hostIds);
            const prevPerHostSummaryPromise = comparisonSupported
                ? HostApi.getHostStatisticV2ById(prevQuery, hostIds)
                : Promise.resolve([] as HostStatisticV2[]);

            try {
                const [hostMappings, allMappings, perHostSummary, prevPerHostSummary] = await Promise.all([
                    hostMappingsPromise,
                    allMappingsPromise,
                    perHostSummaryPromise,
                    prevPerHostSummaryPromise,
                ]);
                if (!active) return;

                // Widget aggregates — average across the hosts attached to this instance.
                const linkedHostIds = new Set(hostMappings.map(m => Number(m.hostid)));
                const linkedSummary = perHostSummary.filter(row =>
                    row.id !== undefined && linkedHostIds.has(Number(row.id))
                );
                const linkedPrevSummary = prevPerHostSummary.filter(row =>
                    row.id !== undefined && linkedHostIds.has(Number(row.id))
                );

                const cpuValues = linkedSummary.map(r => Math.round(Number(r.cpuutilisation) || 0));
                const memValues = linkedSummary.map(r => Math.round(Number(r.memoryutilisation) || 0));
                setProcessorMetric(cpuValues.length ? Math.round(Helper.getAverageFromArray(cpuValues)) : 0);
                setMemoryMetric(memValues.length ? Math.round(Helper.getAverageFromArray(memValues)) : 0);

                if (comparisonSupported) {
                    const prevCpu = linkedPrevSummary.map(r => Math.round(Number(r.cpuutilisation) || 0));
                    const prevMem = linkedPrevSummary.map(r => Math.round(Number(r.memoryutilisation) || 0));
                    setProcessorMetricPrevious(prevCpu.length ? Math.round(Helper.getAverageFromArray(prevCpu)) : null);
                    setMemoryMetricPrevious(prevMem.length ? Math.round(Helper.getAverageFromArray(prevMem)) : null);
                } else {
                    setProcessorMetricPrevious(null);
                    setMemoryMetricPrevious(null);
                }

                // Charts — per-host time series, one v2 call per host returning every metric.
                const hostsForCharts: HostTarget[] = [];
                hostMappings.forEach(mapping => {
                    const matched = props.hosts.find(host => host.id === mapping.hostid);
                    if (matched) hostsForCharts.push(matched);
                });

                const timeSeriesResponses = await Promise.all(
                    hostsForCharts.map(host =>
                        HostApi.getHostStatisticV2Time(query, [host.id]).then(data => ({ host, data }))
                    )
                );
                if (!active) return;

                const memSeriesNew: any[] = [];
                const procSeriesNew: any[] = [];

                timeSeriesResponses.forEach(({ host, data }) => {
                    procSeriesNew.push({
                        name: `CPU - ${host.name}`,
                        color: COLOUR_HOST_PROCESSOR,
                        chartType: 'line',
                        data: data.map((item): EChartPoint => [getConvertedTimeToUTC(item), Math.round(Number(item.cpuutilisation) || 0)]),
                    });
                    memSeriesNew.push({
                        name: `Memory - ${host.name}`,
                        color: COLOUR_HOST_MEMORY,
                        chartType: 'line',
                        data: data.map((item): EChartPoint => [getConvertedTimeToUTC(item), Math.round(Number(item.memoryutilisation) || 0)]),
                    });
                });

                setProcessorSeries(procSeriesNew);
                setMemorySeries(memSeriesNew);
                setLoading(DATA_LOADED);

                // Table — covers every host in props.hosts, not just those linked to the active instance.
                const statsByHostId: Map<number, HostStatisticV2> = new Map();
                perHostSummary.forEach(row => {
                    if (row.id !== undefined && row.id !== null) {
                        statsByHostId.set(Number(row.id), row);
                    }
                });
                const prevStatsByHostId: Map<number, HostStatisticV2> = new Map();
                prevPerHostSummary.forEach(row => {
                    if (row.id !== undefined && row.id !== null) {
                        prevStatsByHostId.set(Number(row.id), row);
                    }
                });

                const tableData: HostsDataTable[] = props.hosts.map(host => {
                    const stat = statsByHostId.get(Number(host.id));
                    const prevStat = prevStatsByHostId.get(Number(host.id));
                    const matchedInstanceIds = allMappings.filter((item: InstanceTargetHost) => Number(item.hostid) === Number(host.id));
                    const matchedInstances: InstanceTarget[] = matchedInstanceIds.flatMap((item: InstanceTargetHost) =>
                        instances.filter(instance => Number(instance.id) === Number(item.id))
                    );
                    return {
                        id: host.id,
                        name: host.name,
                        type: host.type,
                        processor: stat ? Math.round(Number(stat.cpuutilisation) || 0) : 0,
                        processorPeak: 0,
                        memory: stat ? Math.round(Number(stat.memoryutilisation) || 0) : 0,
                        ioutilisation: stat ? Math.round(Number(stat.ioutilisation) || 0) : 0,
                        previousProcessor: prevStat ? Math.round(Number(prevStat.cpuutilisation) || 0) : null,
                        previousProcessorPeak: null,
                        previousMemory: prevStat ? Math.round(Number(prevStat.memoryutilisation) || 0) : null,
                        previousIoutilisation: prevStat ? Math.round(Number(prevStat.ioutilisation) || 0) : null,
                        instances: matchedInstances,
                    };
                });

                setHostsDataTable(tableData);

                // Backfill CPU peak from the time series responses we already have.
                const peakByHostId: Map<number, number> = new Map();
                timeSeriesResponses.forEach(({ host, data }) => {
                    const cpuValues = data.map(item => Number(item.cpuutilisation) || 0);
                    peakByHostId.set(Number(host.id), Math.round(Helper.getPeakValue(cpuValues)));
                });
                setHostsDataTable(prev => prev.map(row => ({
                    ...row,
                    processorPeak: peakByHostId.get(Number(row.id)) ?? row.processorPeak,
                })));

                setTableLoading(DATA_LOADED);
            } catch (error) {
                console.error('Failed to load instance hosts tab data.', error);
                if (active) {
                    setLoading(DATA_LOADED);
                    setTableLoading(DATA_LOADED);
                }
            }
        }

        void load();
        return () => { active = false; };
    }, [props.hosts, timeRangeContext, props.instance.id, comparisonSupported, instances]);


    const charts = useMemo(() => {
        return (
            <div className="row row-cols-1 row-cols-lg-2">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            CPU Average
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseProcessor" aria-expanded="false"
                               aria-controls="collapseProcessor"/>
                        </div>
                        <div id="collapseProcessor" className="card-body collapse show">
                            <p className="peak-wrapper"><span className="peak">Peak Average CPU: {processorPeak}%</span>
                            </p>

                            <TimeSeriesChart series={processorSeries} tooltipType={CHART_DATA_TYPE.PERCENTAGE}
                                             plotLines={props.chartPlotLines} legend={true}/>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            Memory Average
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseMemory" aria-expanded="false" aria-controls="collapseMemory"/>
                        </div>
                        <div id="collapseMemory" className="card-body collapse show">
                            <p className="peak-wrapper"><span className="peak">Peak Average Memory: {memoryPeak}%</span>
                            </p>
                            <TimeSeriesChart series={memorySeries} tooltipType={CHART_DATA_TYPE.PERCENTAGE}
                                             plotLines={props.chartPlotLines} legend={true}/>
                        </div>
                    </div>
                </div>
            </div>
        );

    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.chartPlotLines, processorSeries, memorySeries, licences, processorPeak, memoryPeak]);

    const hostsTable = useMemo(() => {
        return <HostsTable loading={tableLoading} hostsDataTable={hostsDataTable}/>;
    }, [hostsDataTable, tableLoading])

    return (
        <React.Fragment>

            {/* Metric Widgets - Host */}
            {props.hosts.length > 0 && (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3">
                    <WidgetCard
                        name="CPU Average"
                        type='percent'
                        iconClassName="processor"
                        icon="fa-microchip"
                        value={processorMetric}
                        previous={processorMetricPrevious ?? undefined}
                        cardTooltipText="The average CPU utilization"
                        loading={loading === DATA_LOADING}
                    />
                    <WidgetCard
                        name="Memory Average"
                        type='percent'
                        iconClassName="memory"
                        icon="fa-memory"
                        value={memoryMetric}
                        previous={memoryMetricPrevious ?? undefined}
                        cardTooltipText="The average memory utilization"
                        loading={loading === DATA_LOADING}
                    />
                </div>
            )}

            {/* Host Metrics over Time */}
            {props.hosts.length > 0 && (
                charts
            )}

            {/* Hosts */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-server fa-fw" aria-hidden="true" />
                            Monitored hosts
                            <span className="badge bg-info"
                                  data-tooltip-id="global-tooltip" data-tooltip-content="Total host count">{props.hosts.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseHosts" aria-expanded="false" aria-controls="collapseHosts" />
                        </div>
                        <div id="collapseHosts" className="card-body collapse show">
                            {hostsTable}
                        </div>
                    </div>
                </div>
            </div>

        </React.Fragment>
    );
}
export default Hosts;
